import { ClassicFfmpegClient } from './ffmpeg-client';
import { runtimeAssetPath, validateRuntimeManifest } from './ffmpeg-runtime';

const MIB = 1024 * 1024;
const FRAME_SIZE = 640;
const STATIC_SOURCE_MAX_BYTES = 10 * MIB;
const ANIMATED_SOURCE_MAX_BYTES = 50 * MIB;
const NORMALIZED_FRAME_MAX_BYTES = 20 * MIB;
const FFMPEG_LOAD_TIMEOUT_MS = 120_000;
const FRAME_NORMALIZATION_EVENT = 'krug:frame-normalization';

export interface NormalizedFrameAsset {
  file: File;
  metadata: Record<string, unknown>;
}

function notifyNormalization(
  state: 'start' | 'done' | 'error',
  message: string,
): void {
  window.dispatchEvent(
    new CustomEvent(FRAME_NORMALIZATION_EVENT, { detail: { state, message } }),
  );
}

function extension(fileName: string): string {
  return fileName.toLowerCase().match(/\.([a-z0-9]{1,8})$/u)?.[1] ?? '';
}

function withTimeout<T>(promise: Promise<T>, milliseconds: number, message: string): Promise<T> {
  let timeout: number | undefined;
  return Promise.race([
    promise.finally(() => {
      if (timeout !== undefined) window.clearTimeout(timeout);
    }),
    new Promise<T>((_resolve, reject) => {
      timeout = window.setTimeout(() => reject(new Error(message)), milliseconds);
    }),
  ]);
}

function blobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Не удалось прочитать рамку.'));
    reader.onload = () => {
      if (!(reader.result instanceof ArrayBuffer)) {
        reject(new Error('Не удалось прочитать рамку.'));
        return;
      }
      resolve(new Uint8Array(reader.result));
    };
    reader.readAsArrayBuffer(blob);
  });
}

function canvasPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Не удалось подготовить PNG-рамку.'));
      },
      'image/png',
    );
  });
}

async function normalizeStaticFrame(
  file: File,
  metadata: Record<string, unknown>,
): Promise<NormalizedFrameAsset> {
  if (!file.size || file.size > STATIC_SOURCE_MAX_BYTES) {
    throw new Error('Статичная рамка слишком большая. Уменьшите файл до 10 МБ.');
  }

  const bitmap = await createImageBitmap(file);
  try {
    if (!bitmap.width || !bitmap.height) {
      throw new Error('Не удалось определить размер рамки.');
    }

    if (
      extension(file.name) === 'png' &&
      bitmap.width === FRAME_SIZE &&
      bitmap.height === FRAME_SIZE
    ) {
      return {
        file: new File([file], 'frame.png', { type: 'image/png' }),
        metadata: {
          ...metadata,
          width: FRAME_SIZE,
          height: FRAME_SIZE,
          normalized: true,
          animated: false,
          sourceFileName: file.name,
          sourceWidth: bitmap.width,
          sourceHeight: bitmap.height,
        },
      };
    }

    const side = Math.min(bitmap.width, bitmap.height);
    const sourceX = (bitmap.width - side) / 2;
    const sourceY = (bitmap.height - side) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = FRAME_SIZE;
    canvas.height = FRAME_SIZE;
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) {
      throw new Error('Браузер не смог подготовить рамку.');
    }
    context.clearRect(0, 0, FRAME_SIZE, FRAME_SIZE);
    context.drawImage(
      bitmap,
      sourceX,
      sourceY,
      side,
      side,
      0,
      0,
      FRAME_SIZE,
      FRAME_SIZE,
    );
    const png = await canvasPng(canvas);
    if (png.size > NORMALIZED_FRAME_MAX_BYTES) {
      throw new Error('После обработки рамка всё ещё слишком большая. Уменьшите исходный файл.');
    }
    return {
      file: new File([png], 'frame.png', { type: 'image/png' }),
      metadata: {
        ...metadata,
        width: FRAME_SIZE,
        height: FRAME_SIZE,
        normalized: true,
        animated: false,
        sourceFileName: file.name,
        sourceWidth: bitmap.width,
        sourceHeight: bitmap.height,
      },
    };
  } finally {
    bitmap.close();
  }
}

async function normalizeAnimatedFrame(
  file: File,
  metadata: Record<string, unknown>,
): Promise<NormalizedFrameAsset> {
  if (!file.size || file.size > ANIMATED_SOURCE_MAX_BYTES) {
    throw new Error('Анимированная рамка слишком большая. Уменьшите MOV до 50 МБ.');
  }

  const manifestResponse = await fetch('/ffmpeg-core/manifest.json', { cache: 'no-store' });
  if (!manifestResponse.ok) {
    throw new Error('Не удалось загрузить видеодвижок для подготовки рамки.');
  }
  const manifest = validateRuntimeManifest(await manifestResponse.json());
  const coreURL = `${window.location.origin}${runtimeAssetPath(manifest, 'ffmpeg-core.js')}`;
  const wasmURL = `${window.location.origin}${runtimeAssetPath(manifest, 'ffmpeg-core.wasm')}`;
  const ffmpeg = new ClassicFfmpegClient(() => undefined);
  const sourceName = `frame-source-${crypto.randomUUID()}.mov`;
  const outputName = `frame-normalized-${crypto.randomUUID()}.webm`;

  try {
    await withTimeout(
      ffmpeg.load({ coreURL, wasmURL }),
      FFMPEG_LOAD_TIMEOUT_MS,
      'Подготовка видеодвижка заняла слишком много времени. Попробуйте другую сеть.',
    );
    await ffmpeg.writeFile(sourceName, await blobBytes(file));
    const code = await ffmpeg.exec([
      '-hide_banner',
      '-y',
      '-i',
      sourceName,
      '-vf',
      `scale=${FRAME_SIZE}:${FRAME_SIZE}:force_original_aspect_ratio=increase:flags=lanczos,crop=${FRAME_SIZE}:${FRAME_SIZE},fps=30,setsar=1,format=yuva420p`,
      '-an',
      '-c:v',
      'libvpx-vp9',
      '-pix_fmt',
      'yuva420p',
      '-auto-alt-ref',
      '0',
      '-b:v',
      '0',
      '-crf',
      '32',
      '-deadline',
      'realtime',
      '-cpu-used',
      '6',
      '-threads',
      '1',
      '-metadata:s:v:0',
      'alpha_mode=1',
      outputName,
    ]);
    if (code !== 0) {
      throw new Error(`Не удалось подготовить анимированную рамку (FFmpeg: ${code}).`);
    }
    const output = await ffmpeg.readFile(outputName);
    if (typeof output === 'string' || !output.byteLength) {
      throw new Error('Видеодвижок вернул пустую рамку.');
    }
    const bytes = new Uint8Array(output);
    if (bytes.byteLength > NORMALIZED_FRAME_MAX_BYTES) {
      throw new Error('После обработки рамка всё ещё слишком большая. Уменьшите исходный MOV.');
    }
    return {
      file: new File([bytes], 'frame.webm', { type: 'video/webm' }),
      metadata: {
        ...metadata,
        width: FRAME_SIZE,
        height: FRAME_SIZE,
        normalized: true,
        animated: true,
        sourceFileName: file.name,
      },
    };
  } finally {
    ffmpeg.terminate();
  }
}

export async function normalizeFrameAsset(
  file: File,
  metadata: Record<string, unknown>,
): Promise<NormalizedFrameAsset> {
  const ext = extension(file.name);
  notifyNormalization(
    'start',
    ext === 'mov'
      ? 'Подгоняем анимированную рамку под 640×640… Это может занять некоторое время.'
      : 'Подгоняем рамку под 640×640…',
  );
  try {
    const result =
      ext === 'png' || ext === 'webp'
        ? await normalizeStaticFrame(file, metadata)
        : ext === 'mov'
          ? await normalizeAnimatedFrame(file, metadata)
          : (() => {
              throw new Error('Рамка должна быть PNG, WebP или MOV с alpha-каналом.');
            })();
    notifyNormalization('done', 'Готово: рамка автоматически подготовлена в размере 640×640.');
    return result;
  } catch (error) {
    notifyNormalization(
      'error',
      error instanceof Error ? error.message : 'Не удалось подготовить рамку.',
    );
    throw error;
  }
}
